import { SILENT_ERROR_META } from '@hub/lib/queryMeta';
import { isAuthRedirectError, withSkipUnauthorized } from '@platform/api';
import type { FetchQueryOptions, QueryClient, QueryFunctionContext } from '@tanstack/react-query';
import { type AnyRoute, type AnyValidator, type ErrorRouteComponent, type LoaderFnContext, Outlet } from '@tanstack/react-router';
import { type ComponentType, createElement, type FunctionComponent, type ReactNode } from 'react';
import type { z } from 'zod';
import type { AbsolutePath, DefinedPath, Layout, SearchOf } from './definePath';
import { createRoute, type DomainParents, lazyRouteComponent, redirect, retainSearchParams } from './domain-routes';
import { composePageBeforeLoad, type PageBeforeLoadOptions } from './pageBeforeLoad';
import type { QuickActionMeta } from './quickActions';
import type { RouterAuthState } from './routerAuth';

type LoaderContext = { context: { queryClient: QueryClient }; preload: boolean };

/**
 * Context the authenticated layout exposes to dynamic chrome predicates. Add fields here
 * when a route legitimately needs more.
 *
 * The full union, not `SettledRouterAuth`: this comes from `useAuth()` in the layout, not
 * from the router context. Predicates read the identity via `authUser()`, which answers for
 * every status, and none of them decides routing.
 */
export interface PageRouteContext {
  auth: RouterAuthState;
}

/**
 * Footer chrome variants.
 *
 * Single member for now — extend this union when a new flow needs different chrome
 * (e.g. `'minimal' | 'campaign'`). The `<Footer>` switch in `app/shell/Footer.tsx`
 * narrows on it, so adding a member surfaces the missing branch at type-check time.
 */
export type FooterVariant = 'minimal';
/**
 * Back button navigation strategy. `'in-app'` (default) goes back in the router
 * history if possible, else navigates to `backFallback`. `'legacy'` leaves the SPA for
 * the legacy customer-self-care home (`legacySelfCareHome`) — use it for pages whose
 * parent surface still lives in the old app.
 */
export type BackBehavior = 'legacy' | 'in-app';
/**
 * A domain's TanStack Query options factory (typically an Orval `getGet*QueryOptions`).
 * Data-type slots stay `any` — each domain returns a differently-typed query and
 * `FetchQueryOptions` is invariant there — but the options *shape* is enforced.
 */
// biome-ignore lint/suspicious/noExplicitAny: heterogeneous data-type slots; the query-options shape is still enforced.
type QueryOptionsFactory = () => FetchQueryOptions<any, any, any, any>;
type SearchValidator = AnyValidator;
type LazyComponent = () => Promise<{ default: ComponentType }>;

interface DefinePageRouteOptions<L extends Layout, P extends AbsolutePath, D = unknown, S extends z.ZodType | undefined = undefined>
  extends PageBeforeLoadOptions {
  /** `definePath(...)` result — provides path, layout, id, and the search schema that types `loaderDeps`. */
  path: DefinedPath<L, P, S>;
  /**
   * Override the default parent (`parents[path.layout]`) with another route. Use when this
   * route is a true child in the route tree rather than a flat sibling under the layout
   * shell (e.g. `/account/credentials/update-password` parented under the credentials
   * layout route). When set, the DSL strips the parent's absolute path prefix from
   * `path.path` so TanStack receives the relative segment it expects.
   */
  parent?: AnyRoute;
  /** Single source for browser-tab title, breadcrumb label, and the layout-rendered Page title. */
  title: () => string;
  /** Optional page sub-headline rendered by the authenticated layout's `<Page>`. */
  description?: () => string;
  /** Dynamic import of the page module (must default-export the component). */
  page: LazyComponent;
  /**
   * Skeleton shown while the route's loader is pending. Pass a dynamic-import factory
   * (must default-export the component) to keep skeleton code out of the initial
   * route-tree chunk.
   */
  pendingComponent?: LazyComponent;
  /**
   * Custom error UI. Omit to inherit `defineDomainRoutes`'s default. Pass a
   * dynamic-import factory (must default-export the component).
   */
  errorComponent?: LazyComponent;
  /**
   * TanStack Query options factory. Wrapped as `loader: ({ context }) => prefetchQuery(query())` —
   * non-blocking, so pages with `useQuery` keep their granular loading/error UI instead of
   * suspending the whole route.
   */
  query?: QueryOptionsFactory;
  /** Raw loader. Use for non-query loaders (multi-query, mutations). Takes precedence over `query` only if `query` is unset. */
  loader?: (ctx: LoaderFnContext & { deps: D }) => unknown;
  /**
   * Loader dependency selector. Its inferred return type threads through to `loader`'s `deps`,
   * so the same field types appear at the loader callsite without an `as` cast. `search` is
   * typed from the zod schema declared via `definePath(..., { search })`; a path without one
   * gets `unknown`.
   *
   * The `validateSearch` override below does *not* retype this — `search` always follows
   * `path.search`. Declare the schema on the path so the two can't drift.
   */
  loaderDeps?: (opts: { search: SearchOf<S> }) => D;
  validateSearch?: SearchValidator;
  /**
   * Search keys carried onto navigations into this route when the caller does not set them
   * (TanStack `retainSearchParams`). Use for params that must survive a multi-step flow, so a
   * hop that knows nothing about them cannot drop them.
   */
  retainSearch?: readonly string[];
  /** Disable preload-on-hover. Useful for loaders with side effects. */
  preload?: boolean;
  /** Hide this route from the breadcrumb chain (used by the home route). */
  disableBreadcrumb?: boolean;
  /** Contribute this route to the quick-action registry; consumed by `useQuickActions(surface)`. */
  quickAction?: QuickActionMeta;
  /**
   * Hide the back button. Pass a predicate over auth (or other context) for routes that
   * trap the user mid-flow (e.g., dunning lock). Pure function — no hooks, since the
   * layout invokes it with a snapshot of context, not within a hook scope.
   */
  hideBack?: (ctx: PageRouteContext) => boolean;
  /**
   * Path the back button falls back to when there's no history (e.g., the user landed
   * via a deep link). Useful for mid-flow pages whose natural parent isn't the home page.
   */
  backFallback?: string;
  backBehavior?: BackBehavior;
  /**
   * Last-resort opt-out of the authenticated layout's `<Page>` wrap. Prefer adding the
   * dynamic chrome you need to `PageRouteContext` over reaching for this flag — the
   * goal is for new domains to land canonically, not as one-offs.
   */
  customLayout?: boolean;
  /**
   * Footer variant rendered by `<Footer>`. `'minimal'` drops marketing sections,
   * leaving legal links + social icons + copyright. Add new variants here when a
   * flow needs a different chrome (e.g. campaign landing pages).
   */
  footerVariant?: FooterVariant;
  /**
   * Page-scoped theme. `'dark'` cascades the `.surface-dark` token scope
   * through the layout shell so the reusable footer also picks up the dark palette.
   * `'dark-canvas'` limits that scope to the chrome and leaves the content light.
   */
  theme?: 'dark' | 'dark-canvas';
  shellWidth?: 'wide';
  /**
   * Hide the support link ("Hilfebereich") in the site header. Useful for
   * focused flows (e.g. campaign / quiz pages) that want to keep the user
   * inside the task surface.
   */
  hideHeaderSupport?: boolean;
  hideHeaderUser?: boolean;
}

/**
 * Returns the path segment TanStack expects on the route. With a `parent` override,
 * the absolute path declared via `definePath` is stripped of the parent's prefix so
 * the resulting child path is the leaf segment TanStack composes back under the
 * parent. Index paths (`'/'` produced by `defineIndexPath`) are passed through
 * unchanged. Throws when the declared path does not sit under the given parent —
 * that is a manifest authoring error.
 *
 * Reads the parent's *original* absolute path from `staticData.breadcrumbPath`
 * when available, falling back to TanStack's `options.path`. The fallback only
 * holds for parents at the root of their layout: once a layout is itself nested
 * via `defineLayoutRoute({ parent })`, TanStack stores its relativized leaf
 * segment in `options.path` (e.g. `"credentials"`), and only `breadcrumbPath`
 * still carries the original `/account/credentials` we need to strip against.
 */
function resolveChildPath(path: string, parent: AnyRoute | undefined): string {
  if (parent === undefined) {
    return path;
  }
  if (path === '/') {
    return '/';
  }
  const parentOptions = parent.options as { path?: string; staticData?: { breadcrumbPath?: string } };
  const parentPath = parentOptions.staticData?.breadcrumbPath ?? parentOptions.path;
  if (!parentPath) {
    throw new Error(`definePageRoute: parent of "${path}" declares no path, so there is nothing to relativize against.`);
  }
  return relativizeChildPath(path, parentPath);
}

/**
 * TanStack wants a child's path relative to its parent. Exported for the test harness, which builds
 * chains outside the real tree and must relativize them by the same rule — including the throw, so a
 * chain passed in the wrong order fails instead of mounting something the real tree cannot produce.
 */
export function relativizeChildPath(path: string, parentPath: string): string {
  if (path === '/') {
    return '/';
  }
  if (!path.startsWith(`${parentPath}/`)) {
    throw new Error(`"${path}" is not a child of "${parentPath}": a child's declared path must sit directly under its parent.`);
  }
  return path.slice(parentPath.length + 1);
}

/**
 * Helper for the standard "feature page" route shape: a single component reachable
 * under a localized path, possibly gated by a feature flag, possibly hydrating a
 * query in the loader. Collapses the head/breadcrumb/Page-title trio onto a single
 * `title` source and the `path`/parent-layout pair onto `definePath` output.
 *
 * For routes that don't fit (custom redirects, structural error pages, the
 * mutation-in-loader unsubscribe flow) keep using `createRoute` directly.
 */
export function definePageRoute<
  const L extends Layout,
  const P extends AbsolutePath,
  D = unknown,
  S extends z.ZodType | undefined = undefined,
>(parents: DomainParents, options: DefinePageRouteOptions<L, P, D, S>): AnyRoute {
  const { query, loader, pendingComponent, errorComponent, parent } = options;
  // Prefetches opt out of the global error toast: failures surface inline via
  // the route's errorComponent or the page's local isError handling.
  //
  // 401 needs two workarounds: hover-preload would otherwise trigger the
  // unauthorized handler (we wrap queryFn in `withSkipUnauthorized`), and React
  // Query may dedupe a click onto an in-flight preload — so the active branch
  // re-fetches when it sees a stale skip-scope 401 in the cache.
  const computedLoader: ((ctx: LoaderFnContext & { deps: D }) => unknown) | undefined = query
    ? async ({ context, preload: isPreload }: LoaderContext) => {
        const opts = query();
        const prefetchOpts = { ...opts, meta: { ...opts.meta, ...SILENT_ERROR_META } };

        if (isPreload) {
          // Capture into a const so the narrowing survives into the wrapper closure.
          const queryFn = opts.queryFn;
          const wrappedOpts = {
            ...prefetchOpts,
            queryFn: typeof queryFn === 'function' ? (ctx: QueryFunctionContext) => withSkipUnauthorized(() => queryFn(ctx)) : undefined,
          };
          await context.queryClient.prefetchQuery(wrappedOpts);
          return;
        }

        await context.queryClient.prefetchQuery(prefetchOpts);
        if (isAuthRedirectError(context.queryClient.getQueryState(opts.queryKey)?.error)) {
          // Dedupped from a preload — drop the skip-scope error and refetch in active
          // context so customFetch triggers the redirect. `exact: true` keeps sibling
          // queries that share a key prefix alive.
          context.queryClient.removeQueries({ queryKey: opts.queryKey, exact: true });
          await context.queryClient.prefetchQuery(prefetchOpts);
        }
      }
    : loader;
  // Explicit override on the manifest wins; otherwise fall back to the schema declared
  // alongside the path via `definePath(... , { search })`.
  const validateSearch = options.validateSearch ?? options.path.search;
  const beforeLoad = composePageBeforeLoad(options);

  const resolvedPendingComponent = pendingComponent ? lazyRouteComponent(pendingComponent) : undefined;
  const resolvedErrorComponent = errorComponent ? (lazyRouteComponent(errorComponent) as ErrorRouteComponent) : undefined;

  const routePath = resolveChildPath(options.path.path, parent);
  // Index children share their URL with their parent layout, which already contributes
  // the breadcrumb. Re-emitting it from the index produces a duplicate trailing crumb
  // (and worse, the index's fallback href is the localized `match.pathname` while the
  // layout uses the canonical `breadcrumbPath`, so the dedupe-by-href can't catch it).
  const isIndex = (options.path.path as string) === '/';

  return createRoute({
    getParentRoute: () => parent ?? parents[options.path.layout],
    path: routePath,
    head: () => ({
      meta: [{ title: options.title() }, ...(options.description ? [{ name: 'description', content: options.description() }] : [])],
    }),
    staticData: {
      ...(!isIndex && { breadcrumbLabel: options.title }),
      title: options.title,
      ...(options.description !== undefined && { description: options.description }),
      ...(options.flag !== undefined && { featureFlag: options.flag }),
      ...(options.disableBreadcrumb !== undefined && { disableBreadcrumb: options.disableBreadcrumb }),
      ...(options.hideBack !== undefined && { hideBack: options.hideBack }),
      ...(options.backFallback !== undefined && { backFallback: options.backFallback }),
      ...(options.backBehavior !== undefined && { backBehavior: options.backBehavior }),
      ...(options.customLayout !== undefined && { customLayout: options.customLayout }),
      ...(options.footerVariant !== undefined && { footerVariant: options.footerVariant }),
      ...(options.quickAction !== undefined && { quickAction: options.quickAction }),
      ...(options.theme !== undefined && { theme: options.theme }),
      ...(options.shellWidth !== undefined && { shellWidth: options.shellWidth }),
      ...(options.hideHeaderSupport !== undefined && { hideHeaderSupport: options.hideHeaderSupport }),
      ...(options.hideHeaderUser !== undefined && { hideHeaderUser: options.hideHeaderUser }),
    },
    component: lazyRouteComponent(options.page),
    // Cast to `never` so TanStack doesn't infer our runtime context shape into the route's
    // `TBeforeLoadFn` slot — that would conflict with the explicit `loader` cast below.
    beforeLoad: beforeLoad as never,
    ...(resolvedPendingComponent !== undefined && { pendingComponent: resolvedPendingComponent }),
    ...(resolvedErrorComponent !== undefined && { errorComponent: resolvedErrorComponent }),
    ...(validateSearch !== undefined && { validateSearch }),
    ...(options.retainSearch !== undefined && { search: { middlewares: [retainSearchParams([...options.retainSearch])] } }),
    // Cast at the createRoute boundary: TanStack's `loaderDeps`/`loader` carry a deep
    // generic chain rooted in `validateSearch`. The wrapper exposes a single `D` instead,
    // which is enough to thread types between consumer-supplied `loaderDeps` and `loader`
    // but doesn't structurally match TanStack's deeper constraint without a cast here.
    // biome-ignore lint/suspicious/noExplicitAny: see comment above.
    ...(computedLoader !== undefined && { loader: computedLoader as (ctx: LoaderFnContext) => any }),
    // biome-ignore lint/suspicious/noExplicitAny: see comment above.
    ...(options.loaderDeps !== undefined && { loaderDeps: options.loaderDeps as (opts: any) => any }),
    ...(options.preload !== undefined && { preload: options.preload }),
  });
}

type WrapperComponent = () => Promise<{ default: FunctionComponent<{ children: ReactNode }> }>;

interface LayoutRouteBase<L extends Layout, P extends AbsolutePath> {
  /** `definePath(...)` result — provides the absolute path, layout, and id for the layout route. */
  path: DefinedPath<L, P>;
  /** Breadcrumb label rendered for this layout route as the middle of a child's match chain. */
  breadcrumbLabel: () => string;
  /**
   * Override the default parent (`parents[path.layout]`) with another route. Use when a
   * layout is itself nested under another layout (e.g. `/account/credentials` parented
   * under the `/account` layout). When set, the DSL strips the parent's absolute path
   * prefix from `path.path` so TanStack receives the relative segment it expects.
   */
  parent?: AnyRoute;
}

/**
 * Coupled deliberately: the provider is a lazy chunk, and with no fallback the outlet
 * silently blanks while it loads (the router sets no `defaultPendingComponent`).
 */
type WrapperOptions =
  | {
      /**
       * Scoped provider mounted around the layout's `<Outlet />`. Because a layout route
       * doesn't remount when its child route changes, a provider here keeps state across
       * sibling navigation (e.g. the payment subtree's Billwerk SDK persists between the
       * read view and the edit route).
       *
       * Dynamic-import factory (must default-export a component rendering `children`) so the
       * provider — and its dependencies — stay out of the eager route-tree chunk.
       */
      wrapper: WrapperComponent;
      /** Skeleton shown while the `wrapper` chunk loads. Lazy factory, same shape as `definePageRoute`'s. */
      pendingComponent: LazyComponent;
    }
  | { wrapper?: never; pendingComponent?: never };

type DefineLayoutRouteOptions<L extends Layout, P extends AbsolutePath> = LayoutRouteBase<L, P> & WrapperOptions;

/**
 * Layout-only route: no page component (TanStack renders `<Outlet />` by default),
 * no feature flag, no `<Page>` chrome. Use to introduce a true tree-level parent so
 * URL-nested routes (e.g. `/account/credentials/update-password`) match as children
 * of their natural parent and breadcrumbs fall out of the match chain.
 *
 * Add children via `layoutRoute.addChildren([...])` and pass the same `layoutRoute`
 * as the `parent` of each child's `definePageRoute` call.
 */
export function defineLayoutRoute<const L extends Layout, const P extends AbsolutePath>(
  parents: DomainParents,
  options: DefineLayoutRouteOptions<L, P>,
): AnyRoute {
  const Wrapper = options.wrapper ? lazyRouteComponent(options.wrapper) : undefined;
  const resolvedPendingComponent = options.pendingComponent ? lazyRouteComponent(options.pendingComponent) : undefined;
  // `loadRouteChunk` only calls `component.preload?.()`; without forwarding it the wrapper chunk waits
  // for render instead of loading alongside the child's page chunk.
  const component = Wrapper && Object.assign(() => createElement(Wrapper, null, createElement(Outlet)), { preload: Wrapper.preload });
  return createRoute({
    getParentRoute: () => options.parent ?? parents[options.path.layout],
    path: resolveChildPath(options.path.path, options.parent),
    staticData: {
      breadcrumbLabel: options.breadcrumbLabel,
      breadcrumbPath: options.path.path,
    },
    ...(component !== undefined && { component }),
    ...(resolvedPendingComponent !== undefined && { pendingComponent: resolvedPendingComponent }),
  });
}

/**
 * Match a legacy URL and replace-redirect to a new one. Use for IA migrations
 * where old bookmarks/emails must keep working. Forwards validated search if
 * `from` declares a search schema, so query params (token, campaign, etc.)
 * survive the bounce. Always uses `replace: true` so the legacy URL doesn't
 * accumulate in browser history.
 */
export function defineRedirectRoute<const L extends Layout, const P extends AbsolutePath>(
  parents: DomainParents,
  options: { from: DefinedPath<L, P>; to: DefinedPath },
): AnyRoute {
  const { from, to } = options;
  return createRoute({
    getParentRoute: () => parents[from.layout],
    path: from.path,
    ...(from.search !== undefined && { validateSearch: from.search }),
    beforeLoad: ({ search }: { search: Record<string, unknown> }) => {
      throw redirect({
        to: to.path,
        ...(from.search !== undefined && { search }),
        replace: true,
      });
    },
  });
}
